import { useCallback, useEffect, useMemo } from "react";
import { CohereTable, colorsLight, H3, useFeature } from "@coherehealth/common";
import { useSnackbar } from "notistack";
import { useGetUserCasesForQueue, ServiceCase, QueueViewState, DisplayConfig } from "@coherehealth/qm-api";
// eslint-disable-next-line cohere-react/no-mui-styled-import
import { styled, useTheme } from "@material-ui/core/styles";
import { tableColumnsConfig } from "../../util/TableUtil";
import { useIsInternalCohereUser } from "../../util/authorization";
import { getRedirectUrlServiceCaseDetailsFaxIntake } from "util/serviceCase";

interface Props {
  queueView: QueueViewState;
  activeQueueId?: string;
  displayConfig?: DisplayConfig;
}

export default function DashboardHistory({ queueView, activeQueueId, displayConfig }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const {
    loading: serviceCaseSearchLoading,
    error: serviceCaseSearchError,
    data: serviceCases,
    refetch: fetchUserServiceCases,
  } = useGetUserCasesForQueue({ lazy: true, queueId: activeQueueId || "" });

  const isCohereUser = useIsInternalCohereUser();

  const isNewFaxOutcomePageEnabled = useFeature("enableNewFaxOutcomePage");

  const tableColumns = useMemo(
    () => tableColumnsConfig(queueView, "AGENT_DASHBOARD", isCohereUser, displayConfig),
    [queueView, isCohereUser, displayConfig]
  );

  const handleRowClick = useCallback(
    (serviceCase: ServiceCase) => {
      const redirectUrl = getRedirectUrlServiceCaseDetailsFaxIntake(serviceCase, isNewFaxOutcomePageEnabled);
      window.location.assign(redirectUrl);
    },
    [isNewFaxOutcomePageEnabled]
  );
  useEffect(() => {
    if (activeQueueId) {
      fetchUserServiceCases();
    }
  }, [activeQueueId, fetchUserServiceCases]);

  useEffect(() => {
    if (serviceCaseSearchError) {
      enqueueSnackbar("Failed to get work history", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [serviceCaseSearchError, enqueueSnackbar]);

  return (
    <>
      <WorkRow>
        <H3 style={{ marginLeft: theme.spacing(2), marginBottom: -theme.spacing(1), fontWeight: "bold" }}>
          My history
        </H3>
      </WorkRow>
      <CohereTable<ServiceCase>
        loading={serviceCaseSearchLoading}
        tableColumns={tableColumns}
        data={serviceCases || []}
        rowsHeight="calc( 100vh - 413px )"
        withDivider
        wideRows
        containerFontColor={colorsLight.font.light}
        onRowClick={isNewFaxOutcomePageEnabled ? handleRowClick : undefined}
      />
    </>
  );
}

// eslint-disable-next-line cohere-react/no-mui-styled-import
const WorkRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));
